import { Employee } from "../types/response/common";

/** Normalise a name for comparison: lowercase, collapse whitespace. */
function norm(s: string): string {
  return s.trim().toLowerCase().replace(/\s+/g, " ");
}

/**
 * Maps each Excel employee name to a system employee_id.
 *
 * Matching priority (all comparisons are case-insensitive, whitespace-normalised):
 *  1. Exact full_name match
 *  2. first_name + " " + last_name  (skips middle name in API)
 *  3. last_name + ", " + first_name
 *  4. last_name + " " + first_name  (reversed without comma)
 *
 * Returns a map of { [excelName]: employee_id | null }.
 * null means no system match was found.
 */
export function matchScheduleEmployees(
  excelNames: string[],
  apiEmployees: Employee[]
): Record<string, string | null> {
  const byFullName  = new Map<string, string>();
  const byFirstLast = new Map<string, string>();
  const byLastFirst = new Map<string, string>();
  const byLastFirst2 = new Map<string, string>(); // no comma variant

  for (const emp of apiEmployees) {
    const id = emp.employee_id;

    if (emp.full_name)  byFullName.set(norm(emp.full_name), id);

    const first = emp.first_name ?? "";
    const last  = emp.last_name  ?? "";

    const firstLast = norm(`${first} ${last}`);
    if (firstLast) byFirstLast.set(firstLast, id);

    const lastFirst = norm(`${last}, ${first}`);
    if (lastFirst) byLastFirst.set(lastFirst, id);

    const lastFirst2 = norm(`${last} ${first}`);
    if (lastFirst2) byLastFirst2.set(lastFirst2, id);
  }

  const result: Record<string, string | null> = {};

  for (const name of excelNames) {
    const n = norm(name);

    const matched =
      byFullName.get(n) ??
      byFirstLast.get(n) ??
      byLastFirst.get(n) ??
      byLastFirst2.get(n) ??
      null;

    result[name] = matched;

    if (!matched) {
      console.debug(`[matchScheduleEmployees] No match for: "${name}" (normalised: "${n}")`);
    }
  }

  return result;
}
